if (!window.ByteWishlistVariantPicker && window.location.pathname.includes('/apps/wishlistapp')) {
  const BUTTON_SELECTOR = '.byte-wishlist-line .add-to-cart-btn';
  const LINE_SELECTOR = '.byte-wishlist-line';
  const DIALOG_ID = 'ByteWishlistVariantPicker';
  const scriptData = document.currentScript?.dataset || {};

  // Wishlisted variant id → variant picked in the dialog. The app's handleAddToCart() keeps working
  // with the wishlisted variant id (so the line is still removed from the wishlist afterwards); only
  // the global addToCart() it calls is redirected to the picked variant.
  const variantOverrides = new Map();
  let isAddToCartWrapped = false;

  const wrapAddToCart = () => {
    if (isAddToCartWrapped) return true;
    if (typeof window.addToCart !== 'function') return false;

    const appAddToCart = window.addToCart;
    window.addToCart = (variantId) => appAddToCart(variantOverrides.get(String(variantId)) ?? variantId);
    isAddToCartWrapped = true;
    return true;
  };

  const addVariantToCart = async (button, variantId) => {
    if (typeof window.handleAddToCart !== 'function') return;

    const wishlistVariantId = button.dataset.variantId;
    if (!variantId || String(variantId) === wishlistVariantId) {
      await window.handleAddToCart(button);
      return;
    }

    if (wrapAddToCart()) {
      variantOverrides.set(wishlistVariantId, String(variantId));
      try {
        await window.handleAddToCart(button);
      } finally {
        variantOverrides.delete(wishlistVariantId);
      }
      return;
    }

    // The app no longer exposes addToCart(): add the picked variant through the app anyway; the
    // wishlist line then stays because the app removes by the (swapped) variant id.
    button.dataset.variantId = variantId;
    try {
      await window.handleAddToCart(button);
    } finally {
      button.dataset.variantId = wishlistVariantId;
    }
  };

  class ByteWishlistVariantPicker {
    constructor() {
      this.button = null;
      this.line = null;
      this.product = null;
      this.prices = {};
      this.selectedOptions = [];
      this.dialog = this.#render();
      document.body.appendChild(this.dialog);
      this.#bindEvents();
    }

    open(button, { product, prices }) {
      this.button = button;
      this.line = button.closest(LINE_SELECTOR);
      this.product = product;
      this.prices = prices || {};

      const wishlistVariant = product.variants.find((variant) => String(variant.id) === button.dataset.variantId);
      const initialVariant = wishlistVariant || product.variants.find((variant) => variant.available) || product.variants[0];
      this.selectedOptions = [...initialVariant.options];

      this.title.textContent = this.line?.querySelector('.product-title')?.textContent.trim() || '';
      this.#renderOptions();
      this.#update();

      document.body.classList.add('overflow-hidden');
      this.dialog.showModal();
      // showModal() focuses the first focusable control (close). On mobile that can leave a
      // :focus-visible ring; move focus to the dialog so the ring only appears on keyboard tab.
      this.dialog.focus({ preventScroll: true });
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    get selectedVariant() {
      return this.product?.variants.find((variant) =>
        variant.options.every((value, index) => value === this.selectedOptions[index])
      );
    }

    #render() {
      const dialog = document.createElement('dialog');
      dialog.id = DIALOG_ID;
      dialog.className = 'byte-wishlist-variant-picker';
      dialog.tabIndex = -1;
      dialog.setAttribute('aria-labelledby', `${DIALOG_ID}-Title`);
      dialog.innerHTML = `
        <div class="byte-wishlist-variant-picker__inner">
          <button type="button" class="byte-wishlist-variant-picker__close" data-picker-close>
            <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="20" height="20">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" fill="none"/>
            </svg>
          </button>
          <div class="byte-wishlist-variant-picker__media">
            <img src="" alt="" width="400" height="400" loading="lazy">
          </div>
          <form class="byte-wishlist-variant-picker__content" novalidate>
            <p class="byte-wishlist-variant-picker__heading"></p>
            <h2 class="byte-wishlist-variant-picker__title h4" id="${DIALOG_ID}-Title"></h2>
            <p class="byte-wishlist-variant-picker__price"></p>
            <div class="byte-wishlist-variant-picker__options"></div>
            <button type="submit" class="byte-wishlist-variant-picker__submit button button--full-width"></button>
          </form>
        </div>
      `;

      this.closeButton = dialog.querySelector('[data-picker-close]');
      this.closeButton.setAttribute('aria-label', scriptData.closeLabel || 'Close');
      dialog.querySelector('.byte-wishlist-variant-picker__heading').textContent = scriptData.heading || '';
      this.image = dialog.querySelector('img');
      this.form = dialog.querySelector('form');
      this.title = dialog.querySelector('.byte-wishlist-variant-picker__title');
      this.price = dialog.querySelector('.byte-wishlist-variant-picker__price');
      this.options = dialog.querySelector('.byte-wishlist-variant-picker__options');
      this.submitButton = dialog.querySelector('.byte-wishlist-variant-picker__submit');
      return dialog;
    }

    #bindEvents() {
      this.closeButton.addEventListener('click', () => this.close());
      // Clicks on the ::backdrop target the dialog element itself.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.dialog.addEventListener('close', () => document.body.classList.remove('overflow-hidden'));

      this.options.addEventListener('change', (event) => {
        const input = event.target.closest('input[type="radio"]');
        if (!input) return;
        this.selectedOptions[Number(input.dataset.optionIndex)] = input.value;
        this.#update();
      });

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        const variant = this.selectedVariant;
        if (!variant?.available || !this.button) return;

        const button = this.button;
        this.close();
        addVariantToCart(button, variant.id);
      });
    }

    #renderOptions() {
      this.options.replaceChildren(
        ...this.product.options.map((name, optionIndex) => {
          const fieldset = document.createElement('fieldset');
          fieldset.className = 'byte-wishlist-variant-picker__option';

          const legend = document.createElement('legend');
          legend.className = 'form__label';
          legend.textContent = name;
          fieldset.appendChild(legend);

          const values = [...new Set(this.product.variants.map((variant) => variant.options[optionIndex]))];
          values.forEach((value, valueIndex) => {
            const id = `${DIALOG_ID}-${optionIndex}-${valueIndex}`;
            const input = document.createElement('input');
            input.type = 'radio';
            input.id = id;
            input.name = `${DIALOG_ID}-option-${optionIndex}`;
            input.value = value;
            input.dataset.optionIndex = optionIndex;
            input.className = 'byte-wishlist-variant-picker__input visually-hidden';

            const label = document.createElement('label');
            label.htmlFor = id;
            label.className = 'byte-wishlist-variant-picker__pill';
            label.textContent = value;

            fieldset.append(input, label);
          });

          return fieldset;
        })
      );
    }

    #update() {
      // A value is available when an available variant has it alongside the values selected for the
      // options before it (same rule as the PDP variant picker).
      this.options.querySelectorAll('input[type="radio"]').forEach((input) => {
        const optionIndex = Number(input.dataset.optionIndex);
        input.checked = this.selectedOptions[optionIndex] === input.value;
        const isAvailable = this.product.variants.some(
          (variant) =>
            variant.available &&
            variant.options[optionIndex] === input.value &&
            this.selectedOptions.slice(0, optionIndex).every((value, index) => variant.options[index] === value)
        );
        input.classList.toggle('is-unavailable', !isAvailable);
      });

      const variant = this.selectedVariant;
      const variantStrings = window.variantStrings || {};

      this.price.textContent = variant ? this.prices[variant.id] || '' : '';
      this.image.src = variant?.image || this.line?.querySelector('img')?.src || '';
      this.image.alt = this.title.textContent;

      this.submitButton.disabled = !variant?.available;
      if (!variant) {
        this.submitButton.textContent = variantStrings.unavailable || '';
      } else if (!variant.available) {
        this.submitButton.textContent = variantStrings.soldOut || '';
      } else {
        this.submitButton.textContent = this.button?.textContent.trim() || variantStrings.addToCart || '';
      }
    }
  }

  let picker = null;

  // Runs in the capture phase so it fires before the app's inline onclick="handleAddToCart(this)".
  const handleClick = async (event) => {
    const button = event.target.closest(BUTTON_SELECTOR);
    if (!button) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (button.getAttribute('aria-busy') === 'true') return;

    const api = window.ByteWishlistIcYastik;
    const handle = api?.getHandle(button.closest(LINE_SELECTOR));

    button.setAttribute('aria-busy', 'true');
    const data = handle ? await api.fetchProductData(handle) : null;
    button.removeAttribute('aria-busy');

    if (!data?.product || data.product.variants.length < 2 || typeof HTMLDialogElement !== 'function') {
      addVariantToCart(button, null);
      return;
    }

    picker ||= new ByteWishlistVariantPicker();
    picker.open(button, data);
  };

  window.ByteWishlistVariantPicker = ByteWishlistVariantPicker;
  window.addEventListener('click', handleClick, true);
}
